import Axios from "axios";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { endpoints } from "../../../../../endpoints";
import type { DataMutate } from "../../../../../types/types";
import type {
  PIMProduct,
  SupportedAssetAccessibility,
  SupportedAssetType,
} from "../../../../../types/types.PIM";
import type { EntityProfile, EntityType } from "../../../../../types/types.MDM";
import { getAssetAssociateEndpoint } from "../../../../../util/assetEndpoints";
import type { IAssetPayload } from "../../../../SharedPages/ProductDetailPage/Assets/NewAssetForm";
import {
  accessibilityRequestData,
  NewAssetFormSubmitValues,
} from "../../../../SharedPages/ProductDetailPage/Assets/util";
import { DocumentsOrDigitalMediaTable } from "../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { ExternalLinksTable } from "../../../../SharedPages/ProductDetailPage/Assets/ExternalLinks/ExternalLinksTable";
import { getDateTime } from "../../../../../util/util";

const AssetsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

type EntityDocumentsProps = {
  entity: EntityProfile;
  entityType: EntityType;
  tenantId: string;
  fetchEntityData: DataMutate<EntityProfile>;
  isTabActive?: boolean;
};

function toAssetTableProduct(entity: EntityProfile): PIMProduct {
  return {
    id: entity.id,
    slug: entity.id,
    product_number: null,
    status: entity.status,
    is_editable: entity.status !== "archived",
    pim_product_type: "standard",
    cover_image: entity.cover_image,
    product_schema: {
      generated_assets: [],
    },
  } as unknown as PIMProduct;
}

export function EntityDocuments({
  entity,
  entityType,
  tenantId,
  fetchEntityData,
  isTabActive,
}: EntityDocumentsProps): JSX.Element {
  const { t } = useTranslation();
  const { notifyError } = useContext(Notifications);

  const entityAsProduct = useMemo(() => toAssetTableProduct(entity), [entity]);
  const associateEndpoint = useMemo(
    () => getAssetAssociateEndpoint(entityType, tenantId, entity.id),
    [entity.id, entityType, tenantId]
  );

  const associateAssets = async (
    assetIdsToAdd: string[],
    assetIdsToRemove: string[] = []
  ) => {
    await Axios.patch(associateEndpoint, {
      asset_ids: assetIdsToAdd,
      asset_ids_to_remove: assetIdsToRemove,
    });
    await fetchEntityData();
  };

  const addNewAssetToEntity = async (values: NewAssetFormSubmitValues) => {
    const assetType = values.input_asset_type?.value?.toLowerCase() as
      | SupportedAssetType
      | undefined;
    const categoryId = values.input_category_type?.value;

    if (!assetType || !categoryId) {
      notifyError(t("Could not add asset. Something went wrong."));
      throw new Error("Missing required asset type/category for entity upload");
    }

    const visibility =
      (values.accessibility as SupportedAssetAccessibility | undefined) ??
      "internal";
    const payload: IAssetPayload & {
      visibility: SupportedAssetAccessibility;
    } = {
      name: values.asset_name ?? "",
      asset_type: assetType,
      asset_host_id: values.asset_host.value || "",
      asset_mode: values.asset_source_mode,
      category_id: categoryId,
      language: values.input_lang_type?.value,
      visibility,
      document_type: values.input_document_type?.value || null,
      ...(values.external_asset
        ? { fully_external_url: values.external_asset.url }
        : {}),
      ...accessibilityRequestData(
        assetType,
        visibility,
        values.input_customer_ids ?? [],
        values.input_user_ids ?? [],
        values.input_role_ids ?? []
      ),
    };

    if (values.issue_date) {
      payload.issue_date = getDateTime(values.issue_date, true).dateFullYear;
    }
    if (values.expiration_date) {
      payload.expiration_date = getDateTime(
        values.expiration_date,
        true
      ).dateFullYear;
    }
    if (values.renewal_date) {
      payload.renewal_date = getDateTime(
        values.renewal_date,
        true
      ).dateFullYear;
    }
    if (values.country?.value) {
      payload.country = values.country.value;
    }
    if (values.region?.value) {
      payload.region = values.region.value;
    }

    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_id_pim_assets(tenantId),
        payload
      );
      const assetId = response.data?.id ?? response.data?.data?.id;
      if (!assetId) {
        throw new Error("Asset create response missing id");
      }

      if (values.selected_file) {
        const uploadEndpoint = endpoints.v2_tenants_id_pim_assets_id(
          tenantId,
          assetId
        );
        const formData = new FormData();
        formData.append("file", values.selected_file);
        await Axios.put(uploadEndpoint, formData);
      }

      await associateAssets([assetId]);
    } catch (error) {
      notifyError(t("Could not add asset. Something went wrong."), { error });
      throw error;
    }
  };

  return (
    <AssetsContainer>
      {["Documents", "Digital Media"].map((title) => (
        <DocumentsOrDigitalMediaTable
          key={title}
          title={t(title)}
          product={entityAsProduct}
          fetchProductData={fetchEntityData}
          isShowTableWithBulkAction={true}
          isShowGridAssetWithBulkAction={true}
          assetContainerType={entityType}
          assetContainerId={entity.id}
          onAssociateAssets={associateAssets}
          onRemoveAssociatedAsset={(assetId) => associateAssets([], [assetId])}
          onAddNewAssetData={addNewAssetToEntity}
          replaceProductId={() => { }}
          allowGenerateDocuments={false}
          useSignedAssetDownloadUrl={true}
          removeConfirmationBody={t(
            "Are you sure you want to remove this asset from this entity?"
          )}
          isTabActive={isTabActive}
        />
      ))}
      <ExternalLinksTable
        product={entityAsProduct}
        assetContainerType={entityType}
        assetContainerId={entity.id}
      />
    </AssetsContainer>
  );
}
